import { reviewCampaignSchema } from '@alicerce/contracts';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Dialog, Field, formatBRL, formatDateTime, formatNumber, Input, PageHeader, PageLoader, Select, Stat, Table, Tabs, Td, Textarea, TextField, Th, Tr } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { errorMessage } from '@/lib/api';
import { useDebounced } from '@/lib/hooks';
import { useApi, useSession } from '@/lib/session';
import { DESTINATION_LABEL, targetingSummary, type Campaign, type Creative } from '../ads/format';

const TABS = [
  { key: 'negocios', label: 'Negócios' },
  { key: 'alertas', label: 'Alertas' },
  { key: 'anuncios', label: 'Revisão de anúncios' },
  { key: 'filas', label: 'Filas e dead-letter' },
  { key: 'webhooks', label: 'Webhooks' },
  { key: 'parametros', label: 'Parâmetros' },
] as const;

export function BackofficeLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const active = TABS.find((t) => pathname.startsWith(`/interno/${t.key}`))?.key ?? 'negocios';
  return (
    <div className="space-y-6">
      <PageHeader title="Back-office" description="Ferramentas da equipe Gestora. Tudo aqui fica na auditoria." />
      <Tabs items={[...TABS]} value={active} onChange={(k) => navigate(`/interno/${k}`)} />
      <Outlet />
    </div>
  );
}

export function BackofficeIndex() {
  return <Navigate to="/interno/negocios" replace />;
}

// ---------- Negócios ----------

interface TenantRow {
  id: string;
  tradeName: string;
  slug: string;
  document: string | null;
  status: string;
  createdAt: string;
  trialEndsAt: string | null;
}

export function TenantsSearchPage() {
  const api = useApi();
  const [text, setText] = useState('');
  const q = useDebounced(text.trim());
  const list = useQuery({ queryKey: ['internal', 'tenants', q], queryFn: () => api.get<TenantRow[]>('/internal/tenants', { query: { q: q || undefined } }) });
  return (
    <Card>
      <CardBody>
        <Input type="search" aria-label="Buscar negócio" placeholder="Nome, slug ou CNPJ" value={text} onChange={(e) => setText(e.target.value)} />
      </CardBody>
      {list.isPending ? (
        <PageLoader />
      ) : list.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(list.error)}</Alert>
        </CardBody>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Negócio</Th>
              <Th>Situação</Th>
              <Th>Criado em</Th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((t) => (
              <Tr key={t.id}>
                <Td>
                  <Link to={`/interno/negocios/${t.id}`} className="font-medium text-brand-700 hover:underline">
                    {t.tradeName}
                  </Link>
                  <p className="text-xs text-muted">{t.slug}</p>
                </Td>
                <Td>
                  <Badge>{t.status}</Badge>
                </Td>
                <Td>{formatDateTime(t.createdAt)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

interface TenantDetail {
  tenant: TenantRow & { legalName: string | null; businessType: string; taxRegime: string | null };
  subscription: { status: string; interval: string; currentPeriodEnd: string | null; items: { moduleKey: string; status: string }[] } | null;
  entitlements: { moduleKey: string; status: string; validUntil: string | null }[];
  domains: { id: string; hostname: string; status: string }[];
  invoices: { id: string; kind: string; status: string; amountCents: number; dueDate: string }[];
  members: { id: string; role: string; user?: { name: string; email: string } }[];
}

export function TenantDetailPage() {
  const { id } = useParams();
  const api = useApi();
  const [dialog, setDialog] = useState<'impersonate' | 'reconcile' | null>(null);
  const q = useQuery({ queryKey: ['internal', 'tenant', id], queryFn: () => api.get<TenantDetail>(`/internal/tenants/${id}`) });
  const refresh = useMutation({ mutationFn: () => api.post(`/internal/tenants/${id}/entitlements/refresh`) });
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const { tenant, subscription, entitlements, domains, invoices, members } = q.data;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/interno/negocios" className="text-sm text-muted hover:underline">
            ← Negócios
          </Link>
          <h2 className="text-lg font-semibold">
            {tenant.tradeName} <Badge>{tenant.status}</Badge>
          </h2>
          <p className="text-sm text-muted">
            {tenant.slug} · {tenant.document ?? 'sem documento'} · criado em {formatDateTime(tenant.createdAt)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setDialog('impersonate')}>Entrar como (leitura)</Button>
          <Button variant="secondary" onClick={() => setDialog('reconcile')}>
            Conciliar
          </Button>
          <Button variant="secondary" loading={refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isSuccess ? 'Cache limpo' : 'Recarregar módulos'}
          </Button>
        </div>
      </div>
      {refresh.isError && <Alert tone="danger">{errorMessage(refresh.error)}</Alert>}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Assinatura" />
          <CardBody className="space-y-2 text-sm">
            {subscription ? (
              <>
                <p>
                  {subscription.status} · {subscription.interval} · renova em {formatDateTime(subscription.currentPeriodEnd)}
                </p>
                <ul className="flex flex-wrap gap-1">
                  {entitlements.map((e) => (
                    <li key={e.moduleKey}>
                      <Badge tone={e.status === 'ACTIVE' ? 'success' : e.status === 'READ_ONLY' ? 'warning' : 'neutral'}>
                        {e.moduleKey}: {e.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-muted">Sem assinatura.</p>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Equipe" />
          <ul className="divide-y divide-line text-sm">
            {members.map((m) => (
              <li key={m.id} className="flex justify-between px-5 py-2">
                <span>
                  {m.user?.name} <span className="text-muted">{m.user?.email}</span>
                </span>
                <Badge>{m.role}</Badge>
              </li>
            ))}
          </ul>
        </Card>
        <Card>
          <CardHeader title="Faturas recentes" />
          <Table>
            <tbody>
              {invoices.map((i) => (
                <Tr key={i.id}>
                  <Td>{formatDateTime(i.dueDate)}</Td>
                  <Td>{i.kind}</Td>
                  <Td>{formatBRL(i.amountCents)}</Td>
                  <Td>
                    <Badge>{i.status}</Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card>
          <CardHeader title="Domínios" />
          <CardBody className="text-sm">
            {domains.length === 0 ? (
              <p className="text-muted">Nenhum domínio próprio.</p>
            ) : (
              <ul className="space-y-1">
                {domains.map((d) => (
                  <li key={d.id}>
                    {d.hostname} <Badge>{d.status}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
      {dialog === 'impersonate' && <ImpersonateDialog tenantId={tenant.id} name={tenant.tradeName} onClose={() => setDialog(null)} />}
      {dialog === 'reconcile' && <ReconcileDialog tenantId={tenant.id} onClose={() => setDialog(null)} />}
    </div>
  );
}

function ImpersonateDialog({ tenantId, name, onClose }: { tenantId: string; name: string; onClose: () => void }) {
  const api = useApi();
  const navigate = useNavigate();
  const { selectTenant } = useSession();
  const [reason, setReason] = useState('');
  const start = useMutation({
    mutationFn: () => api.post(`/internal/tenants/${tenantId}/impersonate`, { reason }),
    onSuccess: () => {
      selectTenant(tenantId, { impersonate: true });
      navigate('/');
    },
  });
  return (
    <Dialog open onClose={onClose} title={`Entrar como ${name}`} description="Acesso somente leitura. O cliente vê este acesso e o motivo em Configurações.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          start.mutate();
        }}
      >
        <Field label="Motivo" error={start.error ? errorMessage(start.error) : undefined}>
          {(a11y) => <Textarea {...a11y} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: chamado 1234, cliente não consegue emitir nota" />}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Voltar
          </Button>
          <Button type="submit" loading={start.isPending} disabled={reason.trim().length < 5}>
            Entrar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface ReconcileDiff {
  action: 'replayed' | 'applied' | 'alert' | string;
  [key: string]: unknown;
}

function ReconcileDialog({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const api = useApi();
  const [form, setForm] = useState(() => {
    const now = Date.now();
    const day = (ms: number) => new Date(ms).toISOString().slice(0, 10);
    return { kind: 'payments' as 'payments' | 'fiscal', from: day(now - 7 * 86400_000), to: day(now) };
  });
  const run = useMutation({
    mutationFn: () =>
      api.post<{ diffs: ReconcileDiff[] }>(`/internal/tenants/${tenantId}/reconcile`, {
        kind: form.kind,
        from: `${form.from}T00:00:00-03:00`,
        to: `${form.to}T23:59:59-03:00`,
      }),
  });
  return (
    <Dialog open onClose={onClose} title="Conciliar com o provedor" description="Até 31 dias por vez. Repetir é seguro." size="lg">
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="O quê">
            {(a11y) => (
              <Select {...a11y} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as 'payments' | 'fiscal' })}>
                <option value="payments">Pagamentos</option>
                <option value="fiscal">Notas fiscais</option>
              </Select>
            )}
          </Field>
          <TextField label="De" type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
          <TextField label="Até" type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
        </div>
        {run.isError && <Alert tone="danger">{errorMessage(run.error)}</Alert>}
        {run.data &&
          (run.data.diffs.length === 0 ? (
            <Alert tone="success">Nenhuma diferença no período.</Alert>
          ) : (
            <ul className="max-h-72 space-y-2 overflow-auto text-xs">
              {run.data.diffs.map((d, i) => (
                <li key={i} className="rounded border border-line p-2">
                  <Badge tone={d.action === 'alert' ? 'danger' : 'success'}>{d.action}</Badge>
                  <pre className="mt-1 whitespace-pre-wrap">{JSON.stringify(d, null, 2)}</pre>
                </li>
              ))}
            </ul>
          ))}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button loading={run.isPending} onClick={() => run.mutate()}>
            Conciliar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------- Alertas ----------

interface SystemAlert {
  id: string;
  tenantId: string | null;
  kind: string;
  message: string;
  details: unknown;
  resolvedAt: string | null;
  createdAt: string;
}

export function AlertsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [all, setAll] = useState(false);
  const q = useQuery({ queryKey: ['internal', 'alerts', all], queryFn: () => api.get<SystemAlert[]>('/internal/alerts', { query: { all: all ? 'true' : undefined } }) });
  const resolve = useMutation({
    mutationFn: (id: string) => api.post(`/internal/alerts/${id}/resolve`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['internal', 'alerts'] }),
  });
  return (
    <Card>
      <CardHeader
        title="Alertas"
        description="O que fazer em cada tipo está no runbook de operação."
        actions={
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Mostrar resolvidos
          </label>
        }
      />
      {resolve.isError && (
        <div className="px-5 pt-4">
          <Alert tone="danger">{errorMessage(resolve.error)}</Alert>
        </div>
      )}
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(q.error)}</Alert>
        </CardBody>
      ) : q.data.length === 0 ? (
        <CardBody>
          <p className="text-sm text-muted">Nenhum alerta aberto.</p>
        </CardBody>
      ) : (
        <ul className="divide-y divide-line">
          {q.data.map((a) => (
            <li key={a.id} className="space-y-1 px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p>
                  <Badge tone={a.resolvedAt ? 'neutral' : 'danger'}>{a.kind}</Badge> {a.message}
                </p>
                {!a.resolvedAt && (
                  <Button size="sm" variant="secondary" loading={resolve.isPending && resolve.variables === a.id} onClick={() => resolve.mutate(a.id)}>
                    Resolver
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted">
                {formatDateTime(a.createdAt)}
                {a.tenantId && (
                  <>
                    {' · '}
                    <Link to={`/interno/negocios/${a.tenantId}`} className="hover:underline">
                      ver negócio
                    </Link>
                  </>
                )}
              </p>
              {a.details && Object.keys(a.details).length > 0 ? (
                <details>
                  <summary className="cursor-pointer text-xs text-muted">Detalhes</summary>
                  <pre className="mt-1 overflow-auto rounded bg-canvas p-2 text-xs">{JSON.stringify(a.details, null, 2)}</pre>
                </details>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---------- Filas e dead-letter ----------

interface Metrics {
  ordersLastHour: number;
  fiscalLast24h: Record<string, number>;
  webhooksFailedLast24h: number;
  queues: { queue: string; counts: Record<string, number> }[];
  deadLetter: number;
}

export function QueuesPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const metrics = useQuery({ queryKey: ['internal', 'metrics'], queryFn: () => api.get<Metrics>('/internal/metrics'), refetchInterval: 30_000 });
  const dead = useQuery({
    queryKey: ['internal', 'dead-letter'],
    queryFn: () => api.get<{ id: string; queue: string; name: string; error?: string; createdAt: string }[]>('/internal/dead-letter'),
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/internal/dead-letter/${encodeURIComponent(id)}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['internal'] }),
  });
  const m = metrics.data;
  return (
    <div className="space-y-6">
      {m && (
        <div className="grid gap-4 sm:grid-cols-4">
          <Card>
            <CardBody>
              <Stat label="Pedidos na última hora" value={formatNumber(m.ordersLastHour)} />
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat label="Notas em 24 h" value={formatNumber(Object.values(m.fiscalLast24h).reduce((s, n) => s + n, 0))} hint={Object.entries(m.fiscalLast24h).map(([k, v]) => `${k}: ${v}`).join(' · ')} />
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat label="Webhooks com falha (24 h)" value={formatNumber(m.webhooksFailedLast24h)} tone={m.webhooksFailedLast24h ? 'negative' : 'neutral'} />
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat label="Dead-letter" value={formatNumber(m.deadLetter)} tone={m.deadLetter ? 'negative' : 'neutral'} />
            </CardBody>
          </Card>
        </div>
      )}
      {m && (
        <Card>
          <CardHeader title="Filas" />
          <Table>
            <thead>
              <tr>
                <Th>Fila</Th>
                <Th>Esperando</Th>
                <Th>Rodando</Th>
                <Th>Agendados</Th>
                <Th>Falhas</Th>
              </tr>
            </thead>
            <tbody>
              {m.queues.map((q) => (
                <Tr key={q.queue}>
                  <Td className="font-mono text-xs">{q.queue}</Td>
                  <Td>{q.counts.waiting ?? 0}</Td>
                  <Td>{q.counts.active ?? 0}</Td>
                  <Td>{q.counts.delayed ?? 0}</Td>
                  <Td>{q.counts.failed ?? 0}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
      <Card>
        <CardHeader title="Dead-letter" description="Corrija a causa antes de reenviar. Os handlers são idempotentes." />
        {retry.isError && (
          <div className="px-5 pt-4">
            <Alert tone="danger">{errorMessage(retry.error)}</Alert>
          </div>
        )}
        {dead.isPending ? (
          <PageLoader />
        ) : dead.isError ? (
          <CardBody>
            <Alert tone="danger">{errorMessage(dead.error)}</Alert>
          </CardBody>
        ) : dead.data.length === 0 ? (
          <CardBody>
            <p className="text-sm text-muted">Vazia.</p>
          </CardBody>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Job</Th>
                <Th>Erro</Th>
                <Th>Quando</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {dead.data.map((j) => (
                <Tr key={j.id}>
                  <Td className="font-mono text-xs">
                    {j.queue}:{j.name}
                  </Td>
                  <Td className="max-w-md truncate text-xs">{j.error ?? '—'}</Td>
                  <Td>{formatDateTime(j.createdAt)}</Td>
                  <Td className="text-right">
                    <Button size="sm" variant="secondary" loading={retry.isPending && retry.variables === j.id} onClick={() => retry.mutate(j.id)}>
                      Reenviar
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ---------- Webhooks ----------

interface WebhookLog {
  id: string;
  provider: string;
  externalId: string;
  eventType: string;
  status: string;
  error: string | null;
  receivedAt: string;
}

export function WebhooksPage() {
  const api = useApi();
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('FAILED');
  const q = useQuery({
    queryKey: ['internal', 'webhooks', provider, status],
    queryFn: () => api.get<WebhookLog[]>('/internal/webhooks', { query: { provider: provider || undefined, status: status || undefined, limit: 100 } }),
  });
  return (
    <Card>
      <CardBody className="flex flex-wrap gap-3">
        <Select aria-label="Provedor" className="w-44" value={provider} onChange={(e) => setProvider(e.target.value)}>
          <option value="">Todos os provedores</option>
          <option value="asaas">Asaas</option>
          <option value="focusnfe">Focus NFe</option>
          <option value="whatsapp">WhatsApp</option>
          <option value="meta_ads">Meta Ads</option>
          <option value="melhorenvio">Melhor Envio</option>
        </Select>
        <Select aria-label="Situação" className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Todas</option>
          <option value="FAILED">Com falha</option>
          <option value="RECEIVED">Recebidos</option>
          <option value="PROCESSED">Processados</option>
          <option value="IGNORED">Ignorados</option>
        </Select>
      </CardBody>
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(q.error)}</Alert>
        </CardBody>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Recebido</Th>
              <Th>Provedor</Th>
              <Th>Evento</Th>
              <Th>Situação</Th>
              <Th>Erro</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((w) => (
              <Tr key={w.id}>
                <Td className="whitespace-nowrap">{formatDateTime(w.receivedAt)}</Td>
                <Td>{w.provider}</Td>
                <Td className="font-mono text-xs">{w.eventType}</Td>
                <Td>
                  <Badge tone={w.status === 'FAILED' ? 'danger' : w.status === 'PROCESSED' ? 'success' : 'neutral'}>{w.status}</Badge>
                </Td>
                <Td className="max-w-sm truncate text-xs">{w.error ?? ''}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

// ---------- Parâmetros ----------

export function PlatformSettingsPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const q = useQuery({ queryKey: ['internal', 'settings'], queryFn: () => api.get<{ key: string; value: unknown; updatedAt: string }[]>('/internal/settings') });
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (v: { key: string; value: unknown }) => api.put(`/internal/settings/${encodeURIComponent(v.key)}`, { value: v.value }),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ['internal', 'settings'] });
    },
  });
  return (
    <Card>
      <CardHeader title="Parâmetros da plataforma" description="Taxas, mínimos e limites lidos pela API. Mudanças valem na hora e ficam na auditoria." />
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <CardBody>
          <Alert tone="danger">{errorMessage(q.error)}</Alert>
        </CardBody>
      ) : (
        <Table>
          <tbody>
            {q.data.map((s) => (
              <Tr key={s.key}>
                <Td className="font-mono text-xs">{s.key}</Td>
                <Td className="font-mono text-xs">{JSON.stringify(s.value)}</Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ key: s.key, text: JSON.stringify(s.value, null, 2) })}>
                    Editar
                  </Button>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing && (
        <Dialog open onClose={() => setEditing(null)} title={editing.key}>
          <div className="space-y-3">
            <Field label="Valor (JSON)" error={parseError ?? (save.error ? errorMessage(save.error) : undefined)}>
              {(a11y) => <Textarea {...a11y} className="font-mono" rows={6} value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} />}
            </Field>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditing(null)}>
                Cancelar
              </Button>
              <Button
                loading={save.isPending}
                onClick={() => {
                  try {
                    const value: unknown = JSON.parse(editing.text);
                    setParseError(null);
                    save.mutate({ key: editing.key, value });
                  } catch {
                    setParseError('JSON inválido.');
                  }
                }}
              >
                Salvar
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

// ---------- Revisão de anúncios ----------

interface ReviewQueueItem {
  tenantId: string;
  tenantName: string;
  id: string;
  name: string;
  destination: Campaign['destination'];
  dailyBudgetCents: number;
  waitingSince: string;
}

export function AdsReviewQueuePage() {
  const api = useApi();
  const q = useQuery({ queryKey: ['internal', 'ads-reviews'], queryFn: () => api.get<{ items: ReviewQueueItem[] }>('/internal/ads/reviews') });
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  return (
    <Card>
      <CardHeader title="Campanhas esperando revisão" description="Das mais antigas para as mais novas." />
      {q.data.items.length === 0 ? (
        <CardBody>
          <p className="text-sm text-muted">Nenhuma campanha na fila.</p>
        </CardBody>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Campanha</Th>
              <Th>Negócio</Th>
              <Th>Destino</Th>
              <Th>Orçamento/dia</Th>
              <Th>Esperando desde</Th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((c) => (
              <Tr key={c.id}>
                <Td>
                  <Link to={`/interno/anuncios/${c.tenantId}/${c.id}`} className="font-medium text-brand-700 hover:underline">
                    {c.name}
                  </Link>
                </Td>
                <Td>{c.tenantName}</Td>
                <Td>{DESTINATION_LABEL[c.destination] ?? c.destination}</Td>
                <Td>{formatBRL(c.dailyBudgetCents)}</Td>
                <Td>{formatDateTime(c.waitingSince)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

export function AdsReviewDetailPage() {
  const { tenantId, id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['internal', 'ads-review', tenantId, id],
    queryFn: () => api.get<Campaign & { tenant: { id: string; name: string }; creatives: Creative[] }>(`/internal/ads/reviews/${tenantId}/${id}`),
  });
  const [edits, setEdits] = useState<Record<string, { headline: string; primaryText: string }>>({});
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const review = useMutation({
    mutationFn: (body: unknown) => api.post(`/internal/ads/reviews/${tenantId}/${id}`, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['internal', 'ads-reviews'] });
      navigate('/interno/anuncios');
    },
  });
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  const c = q.data;
  const send = (body: { action: 'approve' | 'return'; comment?: string; creatives?: unknown }) => {
    if (body.action === 'return' && (body.comment ?? '').trim().length < 5) {
      setError('Escreva o que o lojista precisa ajustar.');
      return;
    }
    const parsed = reviewCampaignSchema.safeParse(body);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Confira os dados.');
      return;
    }
    setError(null);
    review.mutate(parsed.data);
  };
  const creativeEdits = Object.entries(edits).map(([cid, e]) => ({ id: cid, ...e }));

  return (
    <div className="space-y-6">
      <div>
        <Link to="/interno/anuncios" className="text-sm text-muted hover:underline">
          ← Fila de revisão
        </Link>
        <h2 className="text-lg font-semibold">{c.name}</h2>
        <p className="text-sm text-muted">
          {c.tenant.name} · {DESTINATION_LABEL[c.destination] ?? c.destination} · {formatBRL(c.dailyBudgetCents)}/dia
          {c.durationDays ? ` por ${c.durationDays} dias` : ' sem data de fim'}
        </p>
        <p className="text-sm">Público: {targetingSummary(c.targeting)}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {c.creatives.map((cr) => {
          const e = edits[cr.id] ?? { headline: cr.headline, primaryText: cr.primaryText };
          return (
            <Card key={cr.id}>
              <CardBody className="space-y-3">
                {cr.imageUrls[0] && <img src={cr.imageUrls[0]} alt="" className="aspect-square w-full rounded object-cover" />}
                <TextField label="Título" maxLength={40} value={e.headline} onChange={(ev) => setEdits({ ...edits, [cr.id]: { ...e, headline: ev.target.value } })} />
                <Field label="Texto">{(a11y) => <Textarea {...a11y} maxLength={500} value={e.primaryText} onChange={(ev) => setEdits({ ...edits, [cr.id]: { ...e, primaryText: ev.target.value } })} />}</Field>
              </CardBody>
            </Card>
          );
        })}
      </div>
      <Card>
        <CardBody className="space-y-4">
          {(error ?? review.error) && <Alert tone="danger">{error ?? errorMessage(review.error)}</Alert>}
          <Field label="Comentário para o lojista (para devolver)">{(a11y) => <Textarea {...a11y} value={comment} onChange={(e) => setComment(e.target.value)} />}</Field>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" loading={review.isPending} onClick={() => send({ action: 'return', comment })}>
              Devolver para ajustes
            </Button>
            <Button loading={review.isPending} onClick={() => send({ action: 'approve', creatives: creativeEdits.length ? creativeEdits : undefined })}>
              Aprovar e publicar
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
